import {
    faCalculator,
    faExclamationTriangle,
    faPencilAlt,
    faTrash,
    faComment,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ButtonAtom from "../../../../../components/atoms/button/button";
import { formatQuantityToLocale } from "../../../../../utils/method";
import { setAddLineModal, setDeleteLineModal } from "../../../../../store/slices/aufmassV2Slice";
import { showToast } from "../../../../../utils/method";
import "./ExpandablePositionsTable.scss";

function LineItemV2({
    group,
    line,
    lineIndex,
    groupIndex,
    position,
    isCarriedForward,
    readonly,
    renderCarriedForwardBadge,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { readonly: storeReadonly, positionUnitMap } = useSelector((state) => state.aufmassV2);

    // Use prop readonly or store readonly
    const isReadonly = readonly !== undefined ? readonly : storeReadonly;

    // Use the passed isCarriedForward prop or check line property
    const isLineCarriedForward = isCarriedForward || line.isCarriedForward;

    // Check if this is the last line in the group
    const isLastLineInGroup = group?.lines?.length === 1;

    const { manualQuantity, quantity } = line;

    // Determine if quantities mismatch (small epsilon to account for floating point precision)
    const epsilon = 0.0001;
    const quantityMismatch =
        manualQuantity !== null &&
        quantity !== null &&
        Math.abs(manualQuantity - quantity) > epsilon;

    // Set class based on mismatch status
    const lineItemClass = `line-item group-line ${quantityMismatch ? "quantity-mismatch" : ""}`;

    // Tooltip content for quantity mismatch
    const mismatchTooltip = (
        <Tooltip id={`tooltip-mismatch-${lineIndex}`}>
            <div className="text-start">
                <div className="mb-1">
                    {t("aufmass.quantity-values-different")}
                </div>
                <div className="small">
                    {t("aufmass.aufmass-quantity")}:{" "}
                    <strong>{formatQuantityToLocale(manualQuantity)}</strong>
                </div>
                <div className="small">
                    {t("aufmass.calculated-quantity")}:{" "}
                    <strong>{formatQuantityToLocale(quantity)}</strong>
                </div>
            </div>
        </Tooltip>
    );

    // Handle delete line with validation
    const handleDeleteLine = () => {
        if (isLastLineInGroup) {
            showToast(t("aufmass.cannot-delete-last-line"), { type: "warning" });
            return;
        }

        dispatch(setDeleteLineModal({
            show: true,
            line,
            groupIndex,
            lineIndex
        }));
    };

    return (
        <div
            className={lineItemClass}
        >
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    {/* Only show actions if not in readonly mode and not carried forward */}
                    {!isReadonly && !isLineCarriedForward && (
                        <div className="line-actions me-3">
                            <ButtonAtom
                                className="btn btn-sm btn-link text-primary p-0 me-2"
                                onClick={() =>
                                    dispatch(
                                        setAddLineModal({
                                            show: true,
                                            position,
                                            editData: line,
                                            group,
                                            groupIndex,
                                            lineIndex
                                        }),
                                    )
                                }
                                title={
                                    isLineCarriedForward
                                        ? t(
                                            "aufmass.cannot-edit-carried-forward",
                                        )
                                        : "Edit line"
                                }
                                disabled={isReadonly || isLineCarriedForward}
                            >
                                <FontAwesomeIcon icon={faPencilAlt} size="sm" />
                            </ButtonAtom>
                            <ButtonAtom
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={handleDeleteLine}
                                title={
                                    isLineCarriedForward
                                        ? t("aufmass.cannot-delete-carried-forward")
                                        : isLastLineInGroup
                                            ? t("aufmass.cannot-delete-last-line")
                                            : t("aufmass.delete-line-title")
                                }
                                disabled={isReadonly || isLineCarriedForward || isLastLineInGroup}
                            >
                                <FontAwesomeIcon icon={faTrash} size="sm" />
                            </ButtonAtom>
                        </div>
                    )}
                    <div className="line-text">
                        <span className="line-description">
                            {line.text}
                            {renderCarriedForwardBadge &&
                                renderCarriedForwardBadge(line)}
                        </span>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    {/* Comments icon with count if comments exist */}
                    {line.comments && line.comments.trim() && (
                        <div className="me-3">
                            <Badge
                                bg="light"
                                text="dark"
                                className="d-flex align-items-center am-badge-xs"
                            >
                                <FontAwesomeIcon
                                    icon={faComment}
                                    className="me-1 text-info"
                                    size="sm"
                                />
                                <span>{line.comments}</span>
                            </Badge>
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center">
                    {line.factorOperation && (
                        <div className="line-formula me-3 text-muted">
                            <FontAwesomeIcon
                                icon={faCalculator}
                                className="me-2 text-secondary"
                                size="sm"
                            />
                            <small>{line.factorOperation}</small>
                        </div>
                    )}
                    <div className="line-quantity d-flex align-items-center">
                        {quantityMismatch && (
                            <OverlayTrigger
                                placement="left"
                                overlay={mismatchTooltip}
                            >
                                <span className="me-2">
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className="text-warning"
                                        size="sm"
                                    />
                                </span>
                            </OverlayTrigger>
                        )}
                        <Badge bg={"light"} text={"dark"} className="px-3 py-2">
                            {formatQuantityToLocale(line.quantity)}{" "}
                            {positionUnitMap?.[position.id] || position.unit}
                        </Badge>
                    </div>
                </div>
            </div>
        </div >
    );
}

export default LineItemV2;
